import { Box, Checkbox, Link, Typography } from '@mui/material';
import CopyToClipboardValue from 'shared/_components/copy-to-clipboard-value';
import CloudImage from 'shared/_model/cloud/components/CloudImage';
import FileCloudIcon from 'shared/_model/project-files/FileCloudIcon';
import FileDriveIcon from 'shared/_model/project-files/FileDriveIcon';
import YoutubeFileIcon from 'shared/_model/publish/components/YoutubeFileIcon';
import { SharingType } from 'shared/_model/sharing/types';
import { useSharingId } from 'shared/_model/sharing/utils';
import { DocTypeFiles, DocType, TDocFile } from 'shared/constants/docs';
import { isFinishFile } from 'shared/constants/pouchDB';
import { ROUTE } from 'shared/constants/routes_old';
import { prettyDateNumeric, prettySize } from 'shared/utils/format';
import ActionFileMenu from 'shared/_model/project-files/ActionFileMenu';
import { useProjectContext } from 'shared/_pages/project/provider';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { FILE_TYPE_ICON_MAP } from 'shared/_model/project/const';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import { IconEmpty } from 'shared/_ui/icon';
import {
	ProjectFileContainer,
	ProjectFileDate,
	ProjectFileDateSizeContainer,
	ProjectFileDescription,
	ProjectFileFinishIconsContainer,
	ProjectFileIconsContainer,
	ProjectFileImageContainer,
	ProjectFileSize,
	ProjectFileTypeIcon,
	ProjectFileTitle,
} from 'shared/_model/project-files/project-file/styles';
import DocumentShareIcon from 'shared/_model/sharing/document-share-icon';

type ProjectFileProps = {
	file: TDocFile;
};

function getFileLink(id: string, type: DocTypeFiles) {
	switch (type) {
		case DocType.FILE_FINISH_AUDIO:
			return ROUTE.audios.id(id);
		case DocType.FILE_FINISH_VIDEO:
			return ROUTE.videos.id(id);
		default:
			return ROUTE.files.id(id);
	}
}

export default function ProjectFile({ file }: ProjectFileProps) {
	const { _id: id, created, description, size, title } = file;
	const isFinish = isFinishFile(file.type);
	const isFinishVideo = file.type === DocType.FILE_FINISH_VIDEO;
	const { sharingId } = useSharingId();

	const { selectedFileIds, setSelectedFileIds } = useProjectContext();
	const handleSelectChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSelectedFileIds(file._id, event.target.checked);
		},
		[file._id, setSelectedFileIds]
	);

	const TypeIcon = FILE_TYPE_ICON_MAP[file.type];
	const fileLink = !sharingId ? getFileLink(id, file.type) : ROUTE.share.file.id(sharingId, id);
	const thumbnailCloud = file.thumbnail?.cloud;
	const fileImage = (file as any).image;
	const image = useMemo(() => {
		return thumbnailCloud ?? (fileImage ? { s3: fileImage } : undefined);
	}, [thumbnailCloud, fileImage]);

	return (
		<ProjectFileContainer hasDescription={!!description}>
			{isFinish ? (
				<ProjectFileImageContainer square={!isFinishVideo}>
					<CloudImage image={image} />
				</ProjectFileImageContainer>
			) : (
				<ProjectFileTypeIcon>
					<TypeIcon />
				</ProjectFileTypeIcon>
			)}
			<ProjectFileTitle>
				{isFinish ? (
					<Link
						href={fileLink}
						noWrap
						sx={{
							display: 'block',
							color: DESIGN_COLORS.strong,
							fontWeight: 700,
						}}
					>
						{title}
					</Link>
				) : (
					<Typography
						noWrap
						sx={{
							display: 'block',
							color: DESIGN_COLORS.strong,
							fontWeight: 600,
						}}
					>
						{title}
					</Typography>
				)}
			</ProjectFileTitle>
			<ProjectFileDateSizeContainer>
				<ProjectFileDate>{prettyDateNumeric(created)}</ProjectFileDate>
				<ProjectFileSize>{prettySize(size || 0)}</ProjectFileSize>
			</ProjectFileDateSizeContainer>
			<CopyToClipboardValue
				value={id}
				sx={(theme) => ({
					gridArea: 'id',
					display: 'none',
					[theme.breakpoints.up('sm')]: { display: 'flex' },
					'& .MuiTypography-root': {
						color: DESIGN_COLORS.faint,
						fontFamily: theme.typography.fontFamilyMono,
						fontSize: '0.8125rem',
					},
				})}
			/>
			{description && <ProjectFileDescription>{description}</ProjectFileDescription>}
			<ProjectFileIconsContainer>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							gap: 2,
							alignItems: 'center',
						}}
					>
						<Checkbox checked={selectedFileIds.includes(file._id)} onChange={handleSelectChange} />
						<FileDriveIcon file={file} />
						<FileCloudIcon cloud={file.cloud} />
					</Box>
					<ActionFileMenu file={file} />
				</Box>
				<ProjectFileFinishIconsContainer isFinish={isFinish}>
					{isFinishVideo ? <YoutubeFileIcon youtube={file?.youtube} /> : <IconEmpty />}
					{isFinish ? (
						<DocumentShareIcon sharing={file.sharing} type={SharingType.FILE} />
					) : (
						<IconEmpty />
					)}
				</ProjectFileFinishIconsContainer>
			</ProjectFileIconsContainer>
		</ProjectFileContainer>
	);
}
